import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { ArrowRight } from 'lucide-react';

const PortraitPaintingCoursePage = () => {
    return (
        <div data-testid="portrait-painting-course-page">
            <SEO
                title="Portrait Painting Course Online"
                description="Learn portrait painting online from master artists. Study oil portrait techniques, flesh tone mixing, likeness, and classical head construction with Tony Pro, Jeremy Lipking, and more at Zarolla."
                url="/portrait-painting-course"
                keywords={['portrait painting course', 'portrait painting online', 'oil portrait painting', 'learn portrait painting', 'portrait techniques']}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'WebPage',
                    '@id': 'https://zarolla.com/portrait-painting-course#webpage',
                    url: 'https://zarolla.com/portrait-painting-course',
                    name: 'Portrait Painting Course Online | Zarolla Online Art Academy',
                    description: 'Learn portrait painting online from master artists. Oil portrait techniques, flesh tone mixing, and classical head construction.',
                    isPartOf: { '@id': 'https://zarolla.com/#website' },
                    datePublished: '2007-01-01',
                    dateModified: new Date().toISOString().split('T')[0],
                    inLanguage: 'en-US',
                }}
            />

            <div className="section-gap">
                <div className="container-main">
                    {/* Hero */}
                    <div className="max-w-3xl mb-16">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            Classical portraiture
                        </span>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary leading-tight mt-4">
                            Learn portrait painting online.
                        </h1>
                        <p className="text-muted-foreground text-lg mt-6 max-w-2xl">
                            The portrait is one of the most demanding subjects in painting. At Zarolla, our portrait instruction comes from artists who paint portraits professionally — painters like Tony Pro and Jeremy Lipking, whose work is rooted in the classical tradition and refined through decades of practice.
                        </p>
                    </div>

                    {/* Section 1 */}
                    <section className="py-12 border-t border-border">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                            <div>
                                <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                    What makes a great portrait
                                </h2>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    A successful portrait captures more than a likeness — it reveals something about the subject's character and presence. Achieving this requires mastery of drawing, value, color, and edge control, all working together in service of the image.
                                </p>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Our portrait courses cover the complete process: from the initial head construction and proportional measurement to value massing, flesh tone mixing, and the final refinements that bring a portrait to life. You'll learn how master painters approach each stage, and why certain decisions create stronger results.
                                </p>
                                <p className="text-muted-foreground leading-relaxed">
                                    Whether you're working from a live model or reference, these courses give you a systematic process for painting convincing, expressive portraits in oil.
                                </p>
                            </div>
                            <div className="bg-secondary/30 rounded-sm p-8">
                                <h3 className="font-medium text-primary mb-4">Portrait skills you'll develop:</h3>
                                <ul className="space-y-3 text-muted-foreground">
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Head construction and proportional accuracy</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Flesh tone mixing with a limited palette</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Value structure for modeling the head</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Edge control and lost-and-found technique</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Capturing likeness and expression</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Section 2 */}
                    <section className="py-12 border-t border-border">
                        <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                            The tradition behind the instruction
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Zarolla's portrait instruction draws on the classical tradition of representational painting — the same lineage that produced Sargent, Zorn, and Sorolla. Our instructors trained under master painters in this tradition, and their teaching preserves the methods and insights that make great portraiture possible.
                                </p>
                                <p className="text-muted-foreground leading-relaxed">
                                    Glen Orbik's archived teaching on Zarolla provides a direct connection to the Frank Reilly method of head construction — one of the most systematic and effective approaches to portrait drawing ever developed. Combined with painting instruction from Tony Pro, Jeremy Lipking, and others, Zarolla offers a complete education in portrait painting.
                                </p>
                            </div>
                            <div>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Portrait painting is not learned from a single video. It requires sustained study across multiple skills — drawing, value, color, and composition — that reinforce each other over time. Our multi-session courses are structured to build these skills progressively, so each lesson prepares you for the next.
                                </p>
                                <p className="text-muted-foreground leading-relaxed">
                                    Supplement your course work with our painting workshop videos to see how different artists approach portrait subjects in real time, from first mark to final highlight.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Section 3: CTA */}
                    <section className="py-12 border-t border-border">
                        <div className="max-w-2xl">
                            <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                Begin your portrait study
                            </h2>
                            <p className="text-muted-foreground leading-relaxed mb-8">
                                Explore our courses and workshop videos to find portrait instruction at your level. From foundational head drawing to advanced alla prima portraits, there is content here for every stage of your development.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
                                    <Link to="/courses">
                                        View Portrait Courses <ArrowRight className="ml-2 h-4 w-4" />
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="lg">
                                    <Link to="/workshops">View Painting Videos</Link>
                                </Button>
                                <Button asChild variant="outline" size="lg">
                                    <Link to="/about">Meet the Instructors</Link>
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PortraitPaintingCoursePage;
